import { getEventDefinition, isMilestoneEvent } from "@reactive-resume/dto";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID;
const GOOGLE_ADS_PURCHASE_LABEL = import.meta.env.VITE_GOOGLE_ADS_PURCHASE_LABEL;
const isDev = import.meta.env.DEV;

/**
 * Ads tracking is entirely gated on VITE_GOOGLE_ADS_ID being configured.
 * Unset (the default) means: ad consent signals stay denied, no AW tag is
 * configured, and no conversion is ever sent — the exact pre-ads behaviour.
 * Setting it is the deliberate act that opts the site into ad measurement.
 */
export const isAdsTrackingConfigured = (): boolean => Boolean(GOOGLE_ADS_ID);

/**
 * True when the browser reports it is automated (navigator.webdriver) — CI
 * Playwright against prod and prerender bots counted as real users in GA.
 * Never throws.
 */
export const isAutomatedBrowser = (): boolean => {
  try {
    // Strict compare: typed boolean, but undefined in non-browser/older runtimes.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    return typeof navigator !== "undefined" && navigator.webdriver === true;
  } catch {
    return false;
  }
};

/**
 * True when events must not be sent: no browser (SSR / prerender), an automated
 * browser, or gtag was never installed. Never throws.
 */
const cannotSend = (): boolean =>
  typeof window === "undefined" || isAutomatedBrowser() || typeof window.gtag !== "function";

/**
 * Validate event params against registry (dev-only)
 * Never throws — only warns in console
 */
function validateEventParams(eventName: string, params: Record<string, unknown>): void {
  if (!isDev) return;

  const eventDef = getEventDefinition(eventName);
  if (!eventDef) {
    // eslint-disable-next-line no-console
    console.warn(
      `[GA] Event "${eventName}" not found in registry. Add it to libs/dto/src/analytics.ts`,
    );
    return;
  }

  // Check required params
  for (const requiredParam of eventDef.required) {
    if (!(requiredParam in params)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[GA] Event "${eventName}" is missing required param: "${requiredParam}". Expected: ${eventDef.required.join(", ")}`,
      );
    }
  }
}

/**
 * Track a milestone event (user outcome achieved)
 * Example: signup_complete, first_application_submitted, subscription_changed
 */
export const trackMilestone = (eventName: string, params: Record<string, unknown> = {}) => {
  if (!GA_MEASUREMENT_ID || cannotSend()) return;

  validateEventParams(eventName, params);

  window.gtag("event", eventName, {
    event_category: "milestone",
    ...params,
  });
};

/**
 * Track a journey step event (process step within a flow)
 * Example: job_viewed, question_answered, filter_applied
 */
export const trackJourneyStep = (eventName: string, params: Record<string, unknown> = {}) => {
  if (!GA_MEASUREMENT_ID || cannotSend()) return;

  validateEventParams(eventName, params);

  window.gtag("event", eventName, {
    event_category: "journey_step",
    ...params,
  });
};

/**
 * Track a page view with optional custom metadata
 * Uses page_view event for SPA navigation (GA4 best practice)
 * Strips sensitive query parameters (tokens, codes, etc.) to prevent PII leakage
 */
export const pageView = (url: string, metadata?: Record<string, unknown>) => {
  if (!GA_MEASUREMENT_ID || cannotSend()) return;

  // Strip sensitive query parameters (auth tokens, session codes, API keys)
  const urlObj = new URL(url, window.location.origin);
  const sensitiveParams = ["token", "code", "session_id", "api_key"];

  for (const param of sensitiveParams) {
    urlObj.searchParams.delete(param);
  }

  const cleanPath = urlObj.pathname + urlObj.search;
  const cleanLocation = urlObj.origin + cleanPath;

  // Use page_view event for SPA navigation (GA4 best practice)
  window.gtag("event", "page_view", {
    page_path: cleanPath,
    page_location: cleanLocation,
    ...metadata,
  });
};

/**
 * DEPRECATED: Use trackMilestone or trackJourneyStep instead
 * Kept for backward compatibility — routes to appropriate tracker based on event category
 */
export const trackEvent = (eventName: string, params: Record<string, unknown> = {}) => {
  if (isMilestoneEvent(eventName)) {
    trackMilestone(eventName, params);
  } else {
    trackJourneyStep(eventName, params);
  }
};

/**
 * Initialize Google Analytics
 * Sets Consent Mode v2 default to denied before loading gtag.js (GDPR/ePrivacy compliance)
 */
export const initializeAnalytics = () => {
  // No browser globals (SSR / prerender build step): nothing to initialise.
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!GA_MEASUREMENT_ID || isAutomatedBrowser()) return;

  // CRITICAL: Set consent default BEFORE gtag.js loads
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof window.gtag !== "function") {
    // MUST push the `arguments` object, NOT a rest-args array: gtag.js only
    // processes Arguments entries in the dataLayer and silently ignores plain
    // arrays. Our earlier "clean" (...args) rewrite made every command —
    // consent, config, page_view, all events — invisible to GA, which showed
    // "No data received" despite a perfect-looking dataLayer. This mirrors
    // the official snippet on purpose; do not modernize it.
    // eslint-disable-next-line prefer-rest-params, func-names
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    } as unknown as typeof window.gtag;
  }

  // Implied-consent model (2026-07-15, founder decision): analytics is ON
  // by default and the banner informs rather than asks — "by using the site
  // you accept". A previously stored explicit rejection is still honored
  // (restoreConsent() downgrades to denied). Note for the future: if EEA
  // traffic becomes material, EEA visitors should get an opt-in banner again.
  //
  // Ad signals follow VITE_GOOGLE_ADS_ID rather than being hard-denied: they
  // were denied because we ran no ads, and that premise changes the moment an
  // Ads ID is configured. Enhanced conversions are silently discarded by
  // Google unless ad_user_data is granted, so a hard-denied ad_user_data makes
  // the whole feature a no-op. ad_personalization stays denied regardless —
  // conversion measurement does not need it, and remarketing is not in scope.
  const adConsent = isAdsTrackingConfigured() ? "granted" : "denied";

  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: "denied",
  });

  // Load gtag.js script (consent-gated by Consent Mode v2)
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

  document.head.append(script);

  // Config via the gtag JS API — an injected inline <script> would violate
  // our CSP (script-src has no 'unsafe-inline'), which is exactly how GA
  // stayed silently dead in production.
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  // One gtag.js load serves both properties — the AW tag just needs its own
  // config call. allow_enhanced_conversions is what lets the tag forward the
  // hashed user_data we set at conversion time.
  if (GOOGLE_ADS_ID) {
    window.gtag("config", GOOGLE_ADS_ID, { allow_enhanced_conversions: true });
  }
};

/**
 * SHA-256 hex digest, the format Google expects for `sha256_email_address`.
 *
 * Returns null when SubtleCrypto is unavailable (non-secure context, ancient
 * browser). Callers MUST treat null as "send nothing" — never fall back to
 * transmitting a raw address.
 */
const sha256Hex = async (value: string): Promise<string | null> => {
  // Typed as always-present, but genuinely undefined on non-secure origins —
  // hence the runtime check the type system says is unnecessary.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (typeof globalThis.crypto?.subtle?.digest !== "function") return null;

  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};

/**
 * Report a paid subscription to Google Ads as an enhanced conversion.
 *
 * The email is normalised (trimmed + lowercased, per Google's spec) and hashed
 * in the browser before it leaves the page — the raw address is never sent.
 * Hashing it lets Google match the sale to an ad click even when the click id
 * is missing, which is the common case here: buyers reach this page only by
 * clicking through Lemon Squeezy's confirmation modal, so click-id-only
 * matching would undercount.
 *
 * No-ops unless both the Ads ID and the conversion label are configured.
 */
export const trackAdsPurchaseConversion = async (params: {
  email?: string | null;
  value?: number;
  currency?: string;
  transactionId?: string;
}): Promise<void> => {
  if (!GOOGLE_ADS_ID || !GOOGLE_ADS_PURCHASE_LABEL || cannotSend()) return;

  const email = params.email?.trim().toLowerCase();
  if (email) {
    const hashed = await sha256Hex(email);
    if (hashed) window.gtag("set", "user_data", { sha256_email_address: hashed });
  }

  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
    ...(params.value === undefined ? {} : { value: params.value }),
    ...(params.currency === undefined ? {} : { currency: params.currency }),
    // Google dedupes repeat uploads of the same transaction_id, which is what
    // stops a page refresh on this route counting as a second sale.
    ...(params.transactionId === undefined ? {} : { transaction_id: params.transactionId }),
  });
};
